import { Button, Input } from '@heroui/react';
import React, { forwardRef } from 'react';

import { useAddressInput } from './hooks/useAddressInput';
import { PredictionsList } from './PredictionsList';
import type { AddressChangeHandler, AddressData } from './types';

interface AddressInputProps {
  id?: string;
  name?: string;
  placeholder?: string;
  label?: string;
  isRequired?: boolean;
  onAddressChange: AddressChangeHandler<AddressData>;
  className?: string;
  isCustom?: boolean;
  value?: string;
  onManualInput?: (value: string) => void;
  /**
   * Offer "create custom buyer" as an escape hatch from the search. Rendered in
   * exactly one place at a time: inside the dropdown while it is open, as a
   * button underneath once it closes — the dropdown overlays that button, and
   * showing both was the duplicate in CRM-360.
   */
  showCustomOption?: boolean;
  /** Overrides the default custom-buyer handling (which only clears the form). */
  onCreateCustom?: () => void;
}

export const AddressInput = forwardRef<HTMLInputElement, AddressInputProps>(
  (
    {
      onAddressChange,
      className,
      isCustom = false,
      value,
      onManualInput,
      label,
      isRequired = false,
      id,
      showCustomOption = true,
      onCreateCustom,
      ...inputProps
    },
    ref
  ) => {
    const addressInput = useAddressInput({
      onAddressChange,
      isCustom,
      onManualInput,
      value,
    });

    const handleCreateCustom = onCreateCustom
      ? () => {
          addressInput.hidePredictions();
          onCreateCustom();
        }
      : addressInput.onCreateCustomBuyerClick;

    return (
      <div className={`space-y-2 ${className || ''}`}>
        {label && (
          <label htmlFor={id} className="text-sm font-medium text-foreground">
            {label} {isRequired && <span className="text-danger">*</span>}
          </label>
        )}
        <div className="relative" ref={addressInput.inputContainerRef}>
          <Input
            {...inputProps}
            id={id}
            ref={ref}
            value={value}
            autoComplete="off"
            onChange={addressInput.handleInputChange}
            onFocus={addressInput.showPredictions}
            variant="bordered"
            size="md"
            classNames={{
              inputWrapper:
                'bg-default-100 border-default-300 hover:border-default-400 focus-within:border-primary shadow-sm hover:shadow-md transition-all duration-200',
              input: 'text-foreground bg-transparent px-3 py-2',
            }}
          />
          <div
            ref={addressInput.predictionsListRef}
            data-testid="predictions-container"
          >
            <PredictionsList
              predictions={addressInput.predictions}
              loading={
                addressInput.loadingPredictions ||
                addressInput.loadingPlaceDetails
              }
              error={Boolean(addressInput.predictionsError)}
              errorMessage={addressInput.predictionsErrorMessage}
              onPredictionClick={addressInput.onGooglePlaceSelect}
              showPredictions={addressInput.predictionsAreVisible}
              onCreateCustomBuyerClick={handleCreateCustom}
              showCustomOption={showCustomOption}
            />
          </div>
        </div>
        {showCustomOption && !addressInput.predictionsAreVisible && (
          <Button
            type="button"
            variant="bordered"
            size="sm"
            onPress={handleCreateCustom}
          >
            + Create custom buyer
          </Button>
        )}
      </div>
    );
  }
);
